import { useState } from 'react';
import { toast } from 'react-toastify';
import Dialog from '../../../organisms/Dialog';
import { contractsManagementApi } from '../../../../services/contractsManagementApi';
import { formatDateISO } from '../utils/formatters/formatters';
import { UnderlyingReceivable } from '../types/draweeRisk';

interface Props {
    isOpen: boolean;
    item: UnderlyingReceivable | null;
    creditOperationId: string;
    loading?: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

const MIN_REASON_LENGTH = 10;

const RemoveUnderlyingReceivableModal = ({ isOpen, item, creditOperationId, onCancel, onSuccess }: Props) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isReasonInvalid = reason.trim().length < MIN_REASON_LENGTH;
    const isBusy = submitting;

    const handleCancel = () => {
        if (isBusy) return;
        setReason('');
        onCancel();
    };

    const handleConfirm = async () => {
        if (isReasonInvalid || !item) return;
        setSubmitting(true);
        try {
            await contractsManagementApi.removeUnderlyingReceivable(creditOperationId, item.id, reason.trim());
            toast.success('Título removido com sucesso');
            setReason('');
            onSuccess();
        } catch (e: any) {
            toast.error(e.message || 'Erro ao remover título');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            id="remove-underlying-receivable"
            show={isOpen}
            onClose={handleCancel}
            header="Remover título"
            body={
                <div className="p-4 space-y-4">
                    {item && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Tem certeza que deseja remover o título do sacado <strong>{item.sacado}</strong>{' '}
                            (CNPJ {item.cnpj_sacado}), vencimento {formatDateISO(item.vencimento)}? Ele deixará de contar no
                            cálculo e na cobrança, mas permanecerá visível na lista como removido.
                        </p>
                    )}
                    <div>
                        <label htmlFor="remove-underlying-receivable-reason" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Justificativa <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="remove-underlying-receivable-reason"
                            className="w-full text-sm border border-gray-300 dark:border-slate-600 rounded p-2 dark:bg-slate-700 dark:text-white"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Informe o motivo da remoção (mínimo 10 caracteres)..."
                            disabled={isBusy}
                        />
                    </div>
                </div>
            }
            footer={
                <div className="flex gap-3">
                    <button
                        onClick={handleCancel}
                        disabled={isBusy}
                        className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isBusy || isReasonInvalid}
                        className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Removendo...' : 'Remover'}
                    </button>
                </div>
            }
        />
    );
};

export default RemoveUnderlyingReceivableModal;
